import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquareText, Loader2, CheckCircle2, XCircle, Wand2, Calendar, X, Download } from "lucide-react";
import { Agent } from "../../../types/agentTypes";
import { getAgentConfig, saveAgentConfig } from "../../../services/agentConfigApi";
import { toast } from '../../../../../hooks/use-toast';
import { Button } from '../../../../../components/ui/button';

import { supabase } from '../../../../../integrations/supabase/client';
import { PromptChat } from '../components/PromptChat';
import { BusinessDiscoveryChat } from '../components/BusinessDiscoveryChat';
import { AutoGeneratePrompt } from '../components/AutoGeneratePrompt';
import { CodeEditor } from '../../../../../components/ui/code-editor';
import { agentsApi } from '../../../services/agentsApi';
import { CALENDAR_PROMPT_TEMPLATE } from '../../../constants/calendarTemplate';
import { PLAN_OPTIONS } from '../../../constants/plans';
import {
    triggerExtraction,
    subscribeToExtraction,
    getLatestExtraction,
    ExtractionProgress,
} from '../../../../channels/services/extractionService';

type PersonalityOption = 'amigable' | 'serio' | 'creativo' | 'cool' | 'profesional' | 'divertido' | 'otro';
type ResponseSizeOption = 'conciso' | 'moderado' | 'extensivo';

interface AgentConfigProps {
    agent: Agent | null;
    onSave?: (payload: {
        name?: string;
        shortDescription?: string;
        funnel?: string;
        systemPrompt?: string;
    }) => Promise<void> | void;
}

const AgentConfig: React.FC<AgentConfigProps> = ({ agent: _agent }) => {
    const [selectedPersonality, setSelectedPersonality] = useState<PersonalityOption>('cool');
    const [selectedResponseSize, setSelectedResponseSize] = useState<ResponseSizeOption>('moderado');
    const [maxSplitResponses, setMaxSplitResponses] = useState<number>(1);
    const [groupingTime, setGroupingTime] = useState<number>(5);
    const [plan, setPlan] = useState('');
    const [voice, setVoice] = useState<string>('Alicia');
    const [voiceModel, setVoiceModel] = useState<string>('ElevenLabs');
    const [systemPrompt, setSystemPrompt] = useState('');
    const systemPromptRef = useRef('');
    const [knowledgeBaseInstructions, setKnowledgeBaseInstructions] = useState('');
    const [productosPrompt, setProductosPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [userId, setUserId] = useState<string | null>(null);
    const [historialConversaciones, setHistorialConversaciones] = useState('');
    const [calendarPrompt, setCalendarPrompt] = useState('');
    const [showCalendarPrompt, setShowCalendarPrompt] = useState(false);
    const [documentosNegocio, setDocumentosNegocio] = useState('');
    const [forceDiscoveryChat, setForceDiscoveryChat] = useState(false);
    const [generationMode, setGenerationMode] = useState<'auto' | 'chat' | 'edit' | null>(null);

    // Keep ref in sync so callbacks inside the modal always read the latest prompt
    useEffect(() => { systemPromptRef.current = systemPrompt; }, [systemPrompt]);

    // Extraction state
    const [extractionStatus, setExtractionStatus] = useState<string | null>(null);
    const [extractionProgress, setExtractionProgress] = useState<ExtractionProgress | null>(null);
    const [extracting, setExtracting] = useState(false);
    const [sessionName, setSessionName] = useState<string | null>(null);

    useEffect(() => {
        supabase.auth.getUser().then(({ data: { user } }) => {
            setUserId(user?.id || null);
        });
    }, []);

    // Load agent channel to get session name
    useEffect(() => {
        if (!_agent?.id) return;
        agentsApi.getAgentChannels(_agent.id).then(channels => {
            const primary = channels.find(c => c.nombre_instancia);
            if (primary?.nombre_instancia) {
                setSessionName(primary.nombre_instancia);
            }
        }).catch(() => {});

        // Check for existing extraction
        getLatestExtraction(_agent.id).then(extraction => {
            if (extraction) {
                setExtractionProgress(extraction);
                setExtractionStatus(extraction.status);
            }
        }).catch(() => {});
    }, [_agent?.id]);

    const handleExtractHistory = useCallback(async () => {
        if (!_agent?.id || !userId || !sessionName) return;

        setExtracting(true);
        setExtractionStatus('pending');

        try {
            const { extraction_id } = await triggerExtraction({
                userId,
                agentId: _agent.id,
                sessionName,
            });

            // Subscribe to real-time progress updates
            const unsubscribe = subscribeToExtraction(extraction_id, (data) => {
                setExtractionProgress(data);
                setExtractionStatus(data.status);
                if (data.status === 'completed' || data.status === 'failed') {
                    setExtracting(false);
                    unsubscribe();
                    if (data.status === 'completed') {
                        toast.success('Historial de conversaciones extraido exitosamente');
                    } else {
                        toast.error(data.error_message || 'Error en la extraccion');
                    }
                }
            });
        } catch (error: any) {
            console.error('Error triggering extraction:', error);
            toast.error(error.message || 'Error al iniciar la extraccion');
            setExtracting(false);
            setExtractionStatus(null);
        }
    }, [_agent?.id, userId, sessionName]);

    const handleDownloadHistory = useCallback(() => {
        if (!historialConversaciones) {
            toast.error('No hay historial disponible para descargar');
            return;
        }

        const safeName = ((_agent?.name || 'agente')
            .replace(/[^a-z0-9]+/gi, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase()) || 'agente';
        const date = new Date().toISOString().slice(0, 10);

        const blob = new Blob([historialConversaciones], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `historial-${safeName}-${date}.md`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [historialConversaciones, _agent?.name]);

    const reloadConfig = async () => {
        if (!_agent?.id) return;

        try {
            const config = await getAgentConfig(_agent.id);
            if (config) {
                setSystemPrompt(config.system_prompt || '');
                setKnowledgeBaseInstructions(config.instrucciones_kb || '');
                setProductosPrompt(config.productos || '');
                setMaxSplitResponses(config.max_mensajes || 1);
                setGroupingTime(config.tiempo_agrupacion || 5);
                setHistorialConversaciones((config as Record<string, any>).historial_conversaciones || '');
                setDocumentosNegocio((config as Record<string, any>).documentos_negocio || '');
                const cal = (config as Record<string, any>).prompt_calendario || '';
                setCalendarPrompt(cal);
                setShowCalendarPrompt(!!cal);
            }

            // Cargar el plan desde la fila del agente (tabla agents)
            const agentRow = await agentsApi.getAgent(_agent.id);
            setPlan(agentRow?.plan ?? '');
        } catch (error) {
            console.error('Error recargando configuración:', error);
        }
    };

    useEffect(() => {
        if (!_agent?.id) return;

        setLoading(true);
        reloadConfig().finally(() => {
            setLoading(false);
        });
    }, [_agent?.id]);

    // Auto-open generator modal when config loads with no system prompt and context available
    useEffect(() => {
        if (!loading && !systemPrompt.trim() && generationMode === null && !forceDiscoveryChat) {
            const hasContext = !!(historialConversaciones || documentosNegocio);
            if (hasContext) {
                setGenerationMode('auto');
            }
        }
    }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSaveConfig = async () => {
        if (!_agent?.id) {
            toast.error("No hay agente seleccionado");
            return;
        }

        setSaving(true);
        try {
            await saveAgentConfig({
                agent_id: _agent.id,
                system_prompt: systemPrompt,
                instrucciones_kb: knowledgeBaseInstructions,
                max_mensajes: maxSplitResponses,
                tiempo_agrupacion: groupingTime,
                prompt_calendario: calendarPrompt || null,
            } as any);

            // Guardar el plan en la tabla agents (no en agent_config)
            await agentsApi.updateAgent(_agent.id, { plan: plan || null });

            toast.success("Configuración guardada correctamente");
        } catch (error) {
            console.error('Error guardando configuración:', error);
            toast.error("No se pudo guardar la configuración");
        } finally {
            setSaving(false);
        }
    };

    const personalityOptions: { value: PersonalityOption; label: string }[] = [
        { value: 'amigable', label: 'Amigable' },
        { value: 'serio', label: 'Serio' },
        { value: 'creativo', label: 'Creativo' },
        { value: 'cool', label: 'Cool' },
        { value: 'profesional', label: 'Profesional' },
        { value: 'divertido', label: 'Divertido' },
        { value: 'otro', label: 'Otro' }
    ];

    const responseSizeOptions: { value: ResponseSizeOption; label: string }[] = [
        { value: 'conciso', label: 'Conciso' },
        { value: 'moderado', label: 'Moderado' },
        { value: 'extensivo', label: 'Extensivo' }
    ];

    const OptionCard = ({
        label,
        selected,
        onClick
    }: {
        label: string;
        selected: boolean;
        onClick: () => void;
    }) => (
        <div className="w-full h-16 cursor-pointer" onClick={onClick}>
            <div className={`w-full h-full rounded-lg bg-background relative transition-all flex items-center justify-center ${selected ? 'border-2 border-primary bg-primary/5' : 'border border-border hover:border-primary/50'
                }`}>
                <span className="text-foreground text-center font-medium text-sm px-2">
                    {label}
                </span>
            </div>
        </div>
    );

    return (
        <div className="agent-info-panel flex flex-col lg:flex-row gap-6 lg:gap-8 px-4 sm:px-8 lg:px-12 xl:px-20 pb-12 lg:pb-20">
            {/* Left Panel - System Prompt Editor (60%) */}
            <div className="agent-info-panel-left w-full lg:w-[60%] xl:w-[65%] space-y-6 order-1">
                {/* System Prompt */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-foreground">
                            📝 System Prompt
                        </label>
                        {systemPrompt.trim() && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setForceDiscoveryChat(true); setGenerationMode(null); }}
                                className="h-7 text-xs gap-1.5"
                            >
                                <Wand2 className="w-3.5 h-3.5" />
                                Regenerar con IA
                            </Button>
                        )}
                    </div>
                    {historialConversaciones && (
                        <div className="flex items-center gap-1.5 text-xs text-emerald-600">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Historial de chats disponible para generación</span>
                        </div>
                    )}
                    <CodeEditor
                        value={systemPrompt}
                        onChange={setSystemPrompt}
                        placeholder={`# Rol del Agente

Eres un asistente virtual experto en...

## Objetivo
Tu objetivo principal es...

## Instrucciones
- Responde siempre de manera amable
- Enfócate en resolver las dudas del cliente
- Si no sabes algo, sé honesto

## Restricciones
- No compartas información confidencial
- No hagas promesas que no puedas cumplir

## Notas adicionales
Cualquier información relevante...`}
                        minHeight="500px"
                    />
                </div>

                {/* Prompt de Calendario — only visible when calendar prompt exists */}
                {calendarPrompt.trim() && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-foreground flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Prompt de Calendario
                            </label>
                        </div>
                        <CodeEditor
                            value={calendarPrompt}
                            onChange={(val) => { setCalendarPrompt(val); setShowCalendarPrompt(!!val.trim()); }}
                            placeholder="# Sub-Agente de Calendario — [Nombre del Negocio]"
                            minHeight="300px"
                        />
                    </div>
                )}

                {/* Botón Guardar */}
                <div className="mt-6">
                    <Button
                        onClick={handleSaveConfig}
                        disabled={saving || loading}
                        className="w-full"
                    >
                        {saving ? 'Guardando...' : 'Guardar Configuración'}
                    </Button>
                </div>

                {/* Chat History Extraction Section */}
                {(sessionName || historialConversaciones) && (
                    <div className="mt-6 p-4 bg-muted/20 border border-border rounded-lg space-y-3">
                        <div className="flex items-center gap-2">
                            <MessageSquareText className="w-4 h-4 text-foreground/60" />
                            <h3 className="text-sm font-medium text-foreground/80">
                                Historial de Conversaciones
                            </h3>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Extrae los chats existentes de WhatsApp para entrenar al agente con el estilo de comunicacion del negocio.
                        </p>

                        {/* Progress display */}
                        {extracting && extractionProgress && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-xs text-foreground/70">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    <span>
                                        {extractionStatus === 'fetching_chats' && 'Obteniendo chats...'}
                                        {extractionStatus === 'processing_messages' && `Procesando mensajes... (${extractionProgress.processed_chats}/${extractionProgress.total_chats} chats)`}
                                        {extractionStatus === 'processing_media' && `Procesando multimedia... (${extractionProgress.media_processed}/${extractionProgress.media_total})`}
                                        {extractionStatus === 'generating_markdown' && 'Generando resumen...'}
                                        {extractionStatus === 'pending' && 'Iniciando...'}
                                    </span>
                                </div>
                                {(extractionStatus === 'processing_messages' && extractionProgress.total_chats > 0) && (
                                    <div className="w-full bg-muted rounded-full h-1.5">
                                        <div
                                            className="bg-primary h-1.5 rounded-full transition-all"
                                            style={{ width: `${(extractionProgress.processed_chats / extractionProgress.total_chats) * 100}%` }}
                                        />
                                    </div>
                                )}
                                {(extractionStatus === 'processing_media' && extractionProgress.media_total > 0) && (
                                    <div className="w-full bg-muted rounded-full h-1.5">
                                        <div
                                            className="bg-primary h-1.5 rounded-full transition-all"
                                            style={{ width: `${(extractionProgress.media_processed / extractionProgress.media_total) * 100}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Completed status */}
                        {!extracting && extractionStatus === 'completed' && (
                            <div className="flex items-center gap-2 text-xs text-green-600">
                                <CheckCircle2 className="w-3 h-3" />
                                <span>Historial extraido exitosamente</span>
                            </div>
                        )}

                        {/* Failed status */}
                        {!extracting && extractionStatus === 'failed' && (
                            <div className="flex items-center gap-2 text-xs text-red-500">
                                <XCircle className="w-3 h-3" />
                                <span>{extractionProgress?.error_message || 'Error en la extraccion'}</span>
                            </div>
                        )}

                        {sessionName && (
                            <Button
                                variant="outline"
                                onClick={handleExtractHistory}
                                disabled={extracting || !userId}
                                className="w-full"
                                size="sm"
                            >
                                {extracting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Extrayendo...
                                    </>
                                ) : extractionStatus === 'completed' ? (
                                    <>
                                        <MessageSquareText className="w-4 h-4 mr-2" />
                                        Re-extraer Historial
                                    </>
                                ) : (
                                    <>
                                        <MessageSquareText className="w-4 h-4 mr-2" />
                                        Extraer Historial
                                    </>
                                )}
                            </Button>
                        )}

                        {historialConversaciones && (
                            <Button
                                variant="secondary"
                                onClick={handleDownloadHistory}
                                className="w-full"
                                size="sm"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Descargar historial (.md)
                            </Button>
                        )}
                    </div>
                )}

            </div>

            {/* Vertical Divider */}
            <div className="agent-info-panel-divider hidden lg:block w-[1px] bg-border border border-border flex-shrink-0" />

            {/* Right Panel - Preview and Settings (40%) */}
            <div className="agent-config-right w-full lg:w-[40%] xl:w-[35%] order-2">

                {/* AI Chat - Mode switching: auto | chat | edit */}
                <div className="mb-6 lg:sticky lg:top-4">
                    {/* Mode: Discovery Chat */}
                    {generationMode === 'chat' && (
                        <div className="space-y-2">
                            <button
                                onClick={() => setGenerationMode(null)}
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                            >
                                ← Cambiar modo
                            </button>
                            <BusinessDiscoveryChat
                                onApplyPrompt={(prompt) => {
                                    setSystemPrompt(prompt);
                                    setGenerationMode(null);
                                    setForceDiscoveryChat(false);
                                }}
                                context={{
                                    historial_conversaciones: historialConversaciones,
                                    productos: productosPrompt,
                                    documentos_negocio: documentosNegocio,
                                }}
                            />
                        </div>
                    )}

                    {/* Mode Selection (when no prompt or forceDiscoveryChat) */}
                    {generationMode === null && (!systemPrompt.trim() || forceDiscoveryChat) && (
                        <div className="border border-border rounded-lg bg-background overflow-hidden">
                            <div className="px-4 py-3 border-b border-border bg-muted/30">
                                <span className="text-sm font-semibold text-foreground">Configurar con IA</span>
                            </div>
                            <div className="p-4 space-y-3">
                                <p className="text-xs text-muted-foreground">
                                    Elige cómo crear el system prompt de tu agente.
                                </p>
                                <Button
                                    onClick={() => setGenerationMode('auto')}
                                    className="w-full justify-start gap-2"
                                    size="sm"
                                >
                                    <Wand2 className="w-4 h-4" />
                                    Generar Automáticamente
                                    <span className="ml-auto text-xs opacity-70">Recomendado</span>
                                </Button>
                                <Button
                                    onClick={() => setGenerationMode('chat')}
                                    variant="outline"
                                    className="w-full justify-start gap-2"
                                    size="sm"
                                >
                                    <MessageSquareText className="w-4 h-4" />
                                    Chat de Descubrimiento
                                </Button>
                                {forceDiscoveryChat && (
                                    <button
                                        onClick={() => setForceDiscoveryChat(false)}
                                        className="text-xs text-muted-foreground hover:text-foreground w-full text-center"
                                    >
                                        Volver al editor
                                    </button>
                                )}
                            </div>
                        </div>
                    )}


                    {/* Mode: Prompt Editor (when prompt exists and no forced mode) */}
                    {generationMode === null && systemPrompt.trim() && !forceDiscoveryChat && (
                        <PromptChat
                            systemPrompt={systemPrompt}
                            onApplyEdit={setSystemPrompt}
                            agentName={_agent?.name || 'Sin nombre'}
                            agentId={_agent?.id || ''}
                        />
                    )}
                </div>

                {/* Plan Section - Compact */}
                <details className="group mb-4" open>
                    <summary className="cursor-pointer list-none flex items-center justify-between">
                        <h2 className="text-foreground/60 font-montserrat text-base font-bold">Plan</h2>
                        <span className="text-muted-foreground text-xs group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <p className="text-foreground/60 font-montserrat text-xs font-medium mb-3 mt-2">Plan comercial contratado por el cliente.</p>

                    <div className="space-y-1">
                        <label className="text-foreground/70 font-montserrat text-xs">Plan del cliente</label>
                        <select
                            value={plan}
                            onChange={(e) => setPlan(e.target.value)}
                            className="w-full h-9 px-3 rounded-lg border border-border bg-white text-foreground/70 font-montserrat text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                            <option value="">Sin plan</option>
                            {PLAN_OPTIONS.map((p) => (
                                <option key={p.key} value={p.key}>
                                    {p.label} — ${p.price} ({p.contacts.toLocaleString('es')} contactos)
                                </option>
                            ))}
                        </select>
                    </div>
                </details>

                {/* Comportamiento Section - Compact */}
                <details className="group mb-4" open>
                    <summary className="cursor-pointer list-none flex items-center justify-between">
                        <h2 className="text-foreground/60 font-montserrat text-base font-bold">Comportamiento</h2>
                        <span className="text-muted-foreground text-xs group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <p className="text-foreground/60 font-montserrat text-xs font-medium mb-3 mt-2">Ajustes para controlar cómo agrupar y dividir las respuestas.</p>

                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-foreground/70 font-montserrat text-xs">Máximas respuestas divididas</label>
                            <select
                                value={maxSplitResponses}
                                onChange={(e) => setMaxSplitResponses(Number(e.target.value))}
                                className="w-full h-9 px-3 rounded-lg border border-border bg-white text-foreground/70 font-montserrat text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                <option value={1}>1 mensaje</option>
                                <option value={2}>2 mensajes</option>
                                <option value={3}>3 mensajes</option>
                            </select>
                        </div>

                        <div className="space-y-1">
                            <label className="text-foreground/70 font-montserrat text-xs">Tiempo de agrupación (segundos)</label>
                            <select
                                value={groupingTime}
                                onChange={(e) => setGroupingTime(Number(e.target.value))}
                                className="w-full h-9 px-3 rounded-lg border border-border bg-white text-foreground/70 font-montserrat text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                <option value={1}>1 s</option>
                                <option value={5}>5 s</option>
                                <option value={10}>10 s</option>
                                <option value={15}>15 s</option>
                                <option value={20}>20 s</option>
                                <option value={25}>25 s</option>
                                <option value={30}>30 s</option>
                                <option value={35}>35 s</option>
                                <option value={40}>40 s</option>
                                <option value={45}>45 s</option>
                                <option value={50}>50 s</option>
                                <option value={55}>55 s</option>
                                <option value={60}>60 s</option>
                            </select>
                        </div>
                    </div>
                </details>

                {/* Ajustes de voz Section - Compact */}
                <details className="group mb-4">
                    <summary className="cursor-pointer list-none flex items-center justify-between">
                        <h2 className="text-foreground/60 font-montserrat text-base font-bold">Ajustes de voz</h2>
                        <span className="text-muted-foreground text-xs group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <p className="text-foreground/60 font-montserrat text-xs font-medium mb-3 mt-2">Configura la voz y el modelo de síntesis.</p>

                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-foreground/70 font-montserrat text-xs">Voz</label>
                            <select
                                value={voice}
                                onChange={(e) => setVoice(e.target.value)}
                                className="w-full h-9 px-3 rounded-lg border border-border bg-white text-foreground/70 font-montserrat text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                <option value="Alicia">Alicia</option>
                                <option value="Mateo">Mateo</option>
                                <option value="Sofía">Sofía</option>
                            </select>
                        </div>

                        <div className="space-y-1">
                            <label className="text-foreground/70 font-montserrat text-xs">Modelo de voz</label>
                            <select
                                value={voiceModel}
                                onChange={(e) => setVoiceModel(e.target.value)}
                                className="w-full h-9 px-3 rounded-lg border border-border bg-white text-foreground/70 font-montserrat text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                <option value="ElevenLabs">Eleven Labs</option>
                                <option value="GoogleTTS">Google TTS</option>
                                <option value="AmazonPolly">Amazon Polly</option>
                            </select>
                        </div>
                    </div>
                </details>

                {/* Voz y Tono Section - Compact */}
                <details className="group mb-4">
                    <summary className="cursor-pointer list-none flex items-center justify-between">
                        <h2 className="text-foreground/60 font-montserrat text-base font-bold">Voz y Tono</h2>
                        <span className="text-muted-foreground text-xs group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <p className="text-foreground/60 font-montserrat text-xs font-medium mb-3 mt-2">
                        Dale a tu Agente una personalidad acorde a tu negocio.
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                        {personalityOptions.map((option) => (
                            <OptionCard
                                key={option.value}
                                label={option.label}
                                selected={selectedPersonality === option.value}
                                onClick={() => setSelectedPersonality(option.value)}
                            />
                        ))}
                    </div>
                </details>

                {/* Tamaño Respuestas - Compact */}
                <details className="group">
                    <summary className="cursor-pointer list-none flex items-center justify-between">
                        <h2 className="text-foreground/60 font-montserrat text-base font-bold">Tamaño Respuestas</h2>
                        <span className="text-muted-foreground text-xs group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <p className="text-foreground/60 font-montserrat text-xs font-medium mb-3 mt-2">
                        Controla la extensión de las respuestas de tu Agente.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {responseSizeOptions.map((option) => (
                            <OptionCard
                                key={option.value}
                                label={option.label}
                                selected={selectedResponseSize === option.value}
                                onClick={() => setSelectedResponseSize(option.value)}
                            />
                        ))}
                    </div>
                </details>
            </div>

            {/* ═══ Auto Generate Modal (Portal) ═══ */}
            {generationMode === 'auto' && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    {/* Blurred backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => { setGenerationMode(null); setForceDiscoveryChat(false); }}
                    />
                    {/* Modal card */}
                    <div className="relative bg-white rounded-2xl shadow-xl w-[90%] max-w-xl max-h-[85vh] overflow-y-auto">
                        {/* Close button */}
                        <button
                            onClick={() => { setGenerationMode(null); setForceDiscoveryChat(false); }}
                            className="absolute top-3 right-3 z-10 p-1.5 rounded-full hover:bg-muted transition-colors"
                        >
                            <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                        <AutoGeneratePrompt
                            onApplyPrompt={async (prompt) => {
                                setSystemPrompt(prompt);
                                if (_agent?.id) {
                                    try {
                                        await saveAgentConfig({
                                            agent_id: _agent.id,
                                            system_prompt: prompt,
                                            instrucciones_kb: knowledgeBaseInstructions,
                                            max_mensajes: maxSplitResponses,
                                            tiempo_agrupacion: groupingTime,
                                            prompt_calendario: calendarPrompt || null,
                                        } as any);
                                        toast.success('Prompt aplicado y guardado automáticamente');
                                    } catch {
                                        toast.warning('Prompt aplicado pero no se pudo guardar automáticamente');
                                    }
                                }
                            }}
                            onFlowComplete={() => {
                                setGenerationMode(null);
                                setForceDiscoveryChat(false);
                            }}
                            context={{
                                historial_conversaciones: historialConversaciones,
                                productos: productosPrompt,
                                documentos_negocio: documentosNegocio,
                                prompt_calendario: calendarPrompt || null,
                            }}
                            onApplyCalendarPrompt={async (prompt) => {
                                setCalendarPrompt(prompt);
                                setShowCalendarPrompt(true);
                                if (_agent?.id) {
                                    try {
                                        // Use ref to get latest system prompt (avoids stale closure)
                                        await saveAgentConfig({
                                            agent_id: _agent.id,
                                            system_prompt: systemPromptRef.current,
                                            instrucciones_kb: knowledgeBaseInstructions,
                                            max_mensajes: maxSplitResponses,
                                            tiempo_agrupacion: groupingTime,
                                            prompt_calendario: prompt,
                                        } as any);
                                        toast.success('Prompt de calendario guardado automáticamente');
                                    } catch {
                                        toast.warning('Prompt de calendario aplicado pero no se pudo guardar');
                                    }
                                }
                            }}
                            onUpdateContext={async (field, value) => {
                                if (field === 'documentos_negocio') {
                                    setDocumentosNegocio(value);
                                    // Persist to DB
                                    if (_agent?.id) {
                                        try {
                                            await saveAgentConfig({
                                                agent_id: _agent.id,
                                                system_prompt: systemPromptRef.current || '',
                                                instrucciones_kb: knowledgeBaseInstructions,
                                                max_mensajes: maxSplitResponses,
                                                tiempo_agrupacion: groupingTime,
                                                prompt_calendario: calendarPrompt || null,
                                                documentos_negocio: value,
                                            } as any);
                                        } catch {
                                            // Silent — best effort persistence
                                        }
                                    }
                                }
                            }}
                            onBack={() => { setGenerationMode(null); setForceDiscoveryChat(false); }}
                            agentId={_agent?.id}
                        />
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default AgentConfig;
